"use client";

import Image from "next/image";
import Link from "@/components/ui/Link";
import { useRouter } from "next/navigation";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string;
  customClass?: string;
};

type Props = {
  category?: FieldConfig;
  sectionUrl?: string;
  title?: FieldConfig;
  excerpt?: FieldConfig;
  timeAgo?: FieldConfig;
  imageSrc?: FieldConfig;
  slug?: string;
  imageMetadata?: ImageMetadata;
};

export default function HorizontalBigImageCategoryNewsSearch({
  category = { hidden: false, value: "INDIA", customClass: "" },
  sectionUrl,
  title = { hidden: false, value: "இந்தியாவின் நிலக்கரி உற்பத்தி 8.5 சதவீதம் அதிகரிப்பு; நிலக்கரி அமைச்சகம்", customClass: "" },
  excerpt = { hidden: false, value: "நாட்டின் நிலக்கரி உற்பத்தி 2023 ஏப்ரலில் 8.5 சதவீதம் அதிகரித்து 73.14 கோடி டன்னாக", customClass: "" },
  timeAgo = { hidden: false, value: "15 min ago", customClass: "" },
  imageSrc = { hidden: false, value: FALLBACK_IMAGE, customClass: "" },
  slug,
  imageMetadata,
}: Props) {
  const router = useRouter();

  const titleValue = title?.value || "";
  const excerptValue = excerpt?.value || "";
  const imageSrcValue = getImageUrl(imageSrc?.value) || FALLBACK_IMAGE;
  const categoryValue = category?.value || "";
  const timeAgoValue = getTimeAgo(timeAgo?.value);

  const handleCategoryClick = (e: React.MouseEvent) => {
    if (sectionUrl) {
      e.preventDefault();
      e.stopPropagation();
      router.push(sectionUrl);
    }
  };

  const content = (
    <article className="bg-F8F8F8 overflow-hidden p-4 mb-0">
      <div className="flex flex-col md:flex-row gap-4">
        {/* LEFT: IMAGE */}
        {!imageSrc?.hidden && (
          <div className={`w-full md:w-4/12 lg:w-2.5/5 shrink-0 ${imageSrc?.customClass || ""}`}>
            <div className="relative w-full min-h-[180px] overflow-hidden">
              <Image
                src={imageSrcValue}
                alt={titleValue}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 40vw, 300px"
                className="object-cover w-full h-full"
                style={getFocusPointStyle(imageMetadata)}
                loading="lazy"
                placeholder="blur"
                blurDataURL={BLUR_PLACEHOLDER}
              />
            </div>
          </div>
        )}

        {/* RIGHT: CONTENT */}
        <div className="flex flex-col justify-between lg:min-h-[180px] w-full">
          {/* Top Content */}
          <div className="flex flex-col gap-3">
            {!category?.hidden && (
              <>
                {sectionUrl ? (
                  <span
                    className={`text-14-tamil-600 text-012061 tracking-wide uppercase hover:underline cursor-pointer ${category?.customClass || ""}`}
                    onClick={handleCategoryClick}
                  >
                    {categoryValue}
                  </span>
                ) : (
                  <div className={`text-14-tamil-600 text-012061 tracking-wide uppercase ${category?.customClass || ""}`}>
                    {categoryValue}
                  </div>
                )}
              </>
            )}

            {!title?.hidden && (
 <h3 className={`text-18-tamil-700 md:text-16-tamil-700 text-1B1B1B leading-[1.4] ${title?.customClass || ""}`}>
                {titleValue}
              </h3>
            )}

            {!excerpt?.hidden && (
              <p className={`text-14-tamil-400 text-4D4D4D leading-[1.4] line-clamp-2 ${excerpt?.customClass || ""}`}>
                {excerptValue}
              </p>
            )}
          </div>

          {/* Bottom Footer */}
          <div className="mt-3">
            <div className="flex items-center justify-between">
              {!timeAgo?.hidden && (
                <span className={`text-12-english-500 text-1B1B1B ${timeAgo?.customClass || ""}`}>{timeAgoValue}</span>
              )}
              <MessageIcon
                id="image-news-message-icon"
                slug={slug}
                category={BOOKMARK_CATEGORIES.NEWS}
                title={titleValue}
                thumbnailUrl={imageSrcValue}
                description={excerptValue}
                articleUrl={slug ? `/${slug}` : undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </article>
  );

  if (slug) {
    return (
      <Link href={`/${slug}`} className="block  transition-colors cursor-pointer">
        {content}
      </Link>
    );
  }

  return content;
}
